import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '../store';
import { Sheet } from './Sheet';
import { observationInitialSite, observationSpeciesAt, observationSpeciesId } from '../observation-sites';

/** P318 observation: progress of the active session, report cards, and a start picker (species held by the field herd or an enclosure). */
export function ObserveSheet({ initialSpecies, focusTick, onClose }: { initialSpecies?: string; focusTick?: number; onClose?: () => void }) {
  const { sim, bridge, closePanel } = useStore();
  const ob = sim.observe;

  const [species, setSpecies] = useState('');
  // Herd species read like "axis deer"; reports and sessions use ids like "axis_deer". Compare on one form.
  const norm = observationSpeciesId;
  const [site, setSite] = useState(() => observationInitialSite(sim, initialSpecies));
  const speciesHere = observationSpeciesAt(sim, site);
  const [msg, setMsg] = useState('');
  const startedAt = useRef<number | null>(null);
  // Start only with a species the ranch holds now. A past report's species may be gone; that report stays visible, but is not a start choice.
  const wanted = species || initialSpecies || '';
  const pick = speciesHere.find((sp) => norm(sp) === norm(wanted)) ?? speciesHere.at(0) ?? '';
  const focused = focusTick === undefined ? undefined : ob?.recentReports.find((r) => r.completedTick === focusTick);
  const focusGone = focused !== undefined && !speciesHere.some((sp) => norm(sp) === norm(focused.species));
  useEffect(() => {
    if (ob?.session) { setMsg(''); startedAt.current = null; }
  }, [ob?.session]);
  useEffect(() => {
    if (startedAt.current === null) { return; }
    const t = window.setTimeout(() => {
      if (!sim.observe?.session && startedAt.current !== null) {
        setMsg('Not started. The site must actually hold that species, and only one session runs at a time.');
        startedAt.current = null;
      }
    }, 2500);
    return () => { clearTimeout(t); };
  }, [msg]);
  const start = () => {
    startedAt.current = Date.now(); setMsg('Requesting session...');
    bridge.send('observe.start', { species: pick, site: site === 'field' ? { kind: 'field' } : { kind: 'enclosure', enclosureId: site } });
  };
  return (
    <Sheet label="Observation" onClose={onClose ?? closePanel}>
      <div style="padding:6px 18px 16px">
        <div class="eyebrow eyebrow-rule">Wildlife Reserve - Observation</div>
        <div class="display" style="font-size:24px;margin:8px 0;text-align:center">Observe</div>
        {ob === null && <p style="font-size:13px;color:var(--ui-ink-soft)">Observation sessions open once the scenario starts.</p>}
        {ob?.session && (
          <div class="card" style="padding:12px;margin-top:6px">
            <div class="row-value">Watching {ob.session.species.replace(/_/g, ' ')} {ob.session.site.kind === 'field' ? 'in the field' : `at ${ob.session.site.enclosureId}`}</div>
            <div class="row-label">{ob.session.ticksToComplete} hours to go</div>
            <button class="btn btn-quiet" style="margin-top:8px" onClick={() => { bridge.send('observe.cancel', {}); }}>Cancel (earns nothing)</button>
          </div>
        )}
        {ob && !ob.session && (
          <div style="display:grid;gap:8px;margin-top:6px">
            <select aria-label="Species" value={pick} onChange={(e) => { setSpecies(e.currentTarget.value); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)">
              {speciesHere.length === 0 && <option value="">No animals at this site</option>}
              {speciesHere.map((sp) => <option key={sp} value={sp}>{sp.replace(/_/g, ' ')}</option>)}
            </select>
            <select aria-label="Site" value={site} onChange={(e) => { setSite(e.currentTarget.value); }} style="padding:8px;border:var(--ui-border);border-radius:var(--ui-radius);background:var(--ui-parchment-soft)">
              <option value="field">Field spot (wild herd)</option>
              {sim.enclosures.map((en) => <option key={en.id} value={en.id}>Enclosure {en.id}</option>)}
            </select>
            {speciesHere.length === 0 && <div class="row-label" style="white-space:normal">No animals are currently present at this site. Choose an occupied enclosure or the wild field.</div>}
            <button class="btn btn-primary" disabled={!pick} onClick={start}>Start observing</button>
          </div>
        )}
        {focusGone && <div class="row-label" style="white-space:normal;margin-top:8px">{focused.species.replace(/_/g, ' ')} is not at the selected site now, so this is a past report. Start choices only list animals currently present at this site.</div>}
        {msg && <div role="status" class="pill pill-red" style="display:block;margin-top:8px;white-space:normal;text-align:center">{msg}</div>}
        {ob && ob.recentReports.length > 0 && <div class="eyebrow" style="margin-top:12px">Reports</div>}
        {ob?.recentReports.slice().reverse().map((r) => (
          <div key={r.completedTick} class="card" aria-current={r.completedTick === focusTick ? 'true' : undefined} style={`padding:10px 12px;margin-top:6px${r.completedTick === focusTick ? ';border:2px solid var(--ui-amber, #c9852b)' : ''}`}>
            {r.completedTick === focusTick && <div class="eyebrow">Just finished</div>}
            <div class="row-value" style="text-transform:capitalize">{r.species.replace(/_/g, ' ')} - {r.siteKind}</div>
            {r.notes.map((n) => <div key={n} class="row-label" style="white-space:normal">{n.replace(/_/g, ' ')}</div>)}
            <div class="row-label">+{r.researchPoints} research - spotting bonus {(r.spottingBonusBp / 100).toFixed(1)}%</div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
